import { memo, useCallback, useRef, useState } from 'react';
import { Platform, Pressable, View, type ViewStyle } from 'react-native';
import ReanimatedSwipeable from 'react-native-gesture-handler/ReanimatedSwipeable';

import { Stack } from '@/components/layout';
import { SvgIcon, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { NOTIFICATION_TYPE_LABELS } from '@/features/guardian/notifications/constants';
import type { GuardianNotification } from '@/features/guardian/notifications/types';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';

type NotificationItemProps = {
  item: GuardianNotification;
  showType: boolean;
  onDelete: (id: string) => void;
  onPress: (item: GuardianNotification) => void;
};

const strings = STRINGS.guardian.notifications;
const WEB_STABILIZE_DELETE_LAYER = (Platform.select({
  web: {
    transform: 'translateZ(0)',
    backfaceVisibility: 'hidden',
    willChange: 'transform',
  },
  default: {},
}) ?? {}) as ViewStyle;

export const NotificationItem = memo(function NotificationItem({
  item,
  showType,
  onDelete,
  onPress,
}: NotificationItemProps) {
  const { scale } = useScaling();
  const styles = useStyles();
  const [isSwipeOpen, setIsSwipeOpen] = useState(false);
  const suppressPressRef = useRef(false);

  const handleSwipeStart = useCallback(() => {
    // Pressable may emit onPress on the same finger-up that completes a horizontal swipe.
    // A ref updates synchronously, unlike state, so navigation is blocked before that release.
    suppressPressRef.current = true;
    setIsSwipeOpen(true);
  }, []);

  const handleSwipeClose = useCallback(() => {
    setIsSwipeOpen(false);
    // Keep suppression through the release event that completed the closing/aborted swipe.
    setTimeout(() => {
      suppressPressRef.current = false;
    }, 100);
  }, []);

  const handleItemPress = useCallback(() => {
    if (suppressPressRef.current || isSwipeOpen) return;
    onPress(item);
  }, [isSwipeOpen, item, onPress]);

  const renderDeleteAction = useCallback(
    () => (
      <Pressable
        accessibilityRole="button"
        accessibilityLabel={strings.accessibility.delete(item.title)}
        onPress={() => onDelete(item.id)}
        style={[styles.deleteAction, WEB_STABILIZE_DELETE_LAYER]}>
        <SvgIcon name="IcNotificationTrash" size={scale(20)} color={colors['0']} />
      </Pressable>
    ),
    [item.id, item.title, onDelete, scale, styles.deleteAction],
  );

  return (
    <View style={[styles.shadowContainer, !item.isRead ? styles.shadowContainerUnread : null]}>
      <View
        style={[
          styles.swipeContainer,
          item.isRead ? styles.swipeContainerRead : styles.swipeContainerUnread,
        ]}>
        <ReanimatedSwipeable
          friction={2}
          onSwipeableOpenStartDrag={handleSwipeStart}
          onSwipeableCloseStartDrag={handleSwipeStart}
          onSwipeableClose={handleSwipeClose}
          onSwipeableWillOpen={() => setIsSwipeOpen(true)}
          overshootRight={false}
          rightThreshold={scale(36)}
          renderRightActions={renderDeleteAction}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={item.title}
            onPress={handleItemPress}
            style={({ pressed }) => [
              styles.item,
              !isSwipeOpen ? styles.itemRounded : null,
              item.isRead ? styles.itemRead : styles.itemUnread,
              pressed ? styles.pressed : null,
            ]}>
            <View style={styles.itemIcon}>
              <SvgIcon name={item.icon} size={scale(24)} color={guardianColors['Main/Main']} />
            </View>

            <Stack flex={1} gap={scale(4)} align="flex-start">
              {showType ? (
                <View style={[styles.typeBadge, !item.isRead ? styles.typeBadgeUnread : null]}>
                  <Text
                    variant="Font Guardian App/Subtitle/Subtitle 10 Semibold"
                    style={styles.typeText}>
                    {NOTIFICATION_TYPE_LABELS[item.type]}
                  </Text>
                </View>
              ) : null}
              <Text variant="Font Guardian App/Subtitle/Subtitle 14 Bold" style={styles.title}>
                {item.title}
              </Text>
              <Text
                variant="Font Guardian App/Subtitle/Subtitle 12 Regular"
                style={styles.subtitle}>
                {item.subtitle}
              </Text>
            </Stack>

            {!item.isRead ? <View style={styles.unreadDot} /> : null}
            <Text variant="Font Guardian App/Subtitle/Subtitle 12 Semibold" style={styles.time}>
              {item.timeLabel}
            </Text>
          </Pressable>
        </ReanimatedSwipeable>
      </View>
    </View>
  );
});

const useStyles = createStyles(({ scale }) => ({
  shadowContainer: {
    borderRadius: scale(8),
    backgroundColor: colors['0'],
    shadowColor: '#D5D9E2',
    shadowOffset: { width: 0, height: scale(1) },
    shadowOpacity: 0.3,
    shadowRadius: scale(1.5),
    elevation: 2,
  },
  shadowContainerUnread: {
    backgroundColor: guardianColors['Main/10'],
  },
  swipeContainer: {
    borderRadius: scale(8),
    overflow: 'hidden',
  },
  swipeContainerRead: { backgroundColor: colors['0'] },
  swipeContainerUnread: { backgroundColor: guardianColors['Main/10'] },
  item: {
    minHeight: scale(72),
    flexDirection: 'row',
    alignItems: 'center',
    gap: scale(12),
    padding: scale(12),
    backgroundColor: colors['0'],
  },
  itemRounded: {
    borderRadius: scale(8),
    marginRight: Platform.OS === 'web' ? 1 : 0,
  },
  itemUnread: {
    backgroundColor: guardianColors['Main/10'],
    borderWidth: 1,
    borderColor: guardianColors['5'],
  },
  itemRead: { backgroundColor: colors['0'] },
  itemIcon: {
    width: scale(40),
    height: scale(40),
    borderRadius: scale(8),
    backgroundColor: guardianColors['Main/10'],
    borderWidth: 1,
    borderColor: guardianColors['1'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeBadge: {
    minHeight: scale(24),
    paddingHorizontal: scale(12),
    borderRadius: scale(30),
    backgroundColor: guardianColors['Main/10'],
    alignItems: 'center',
    justifyContent: 'center',
  },
  typeBadgeUnread: { borderWidth: 1, borderColor: guardianColors['5'] },
  typeText: { color: guardianColors.Blue },
  title: { color: colors['10'] },
  subtitle: { color: colors['10'] },
  unreadDot: {
    width: scale(6),
    height: scale(6),
    borderRadius: scale(3),
    backgroundColor: guardianColors['Main/Main'],
  },
  time: { color: guardianColors['6'] },
  deleteAction: {
    width: scale(60),
    alignSelf: 'stretch',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: guardianColors.Error,
  },
  pressed: { opacity: Platform.OS === 'web' ? 1 : 0.72 },
}));
